<template>
  <div class="draft row g-1 wrap">
    <ButtonComponent
      :label="saveState.save.label"
      :loading-label="$t('saving')"
      :success-label="$t('saved')"
      :theme="saveState.save.theme"
      :icon="['fal', saveState.save.icon]"
      :promise="savePromise"
      :class="{
        'elder-button--disabled': saveState.save.disabled,
        'elder-button--invalid': saveState.save.invalid,
      }"
      @click="
        () => {
          if (saveState.save.disabled || saveState.save.invalid) return
          savePromise = saveDraft()
        }
      "
      v-tooltip="{ content: saveState.save.tooltip }"
    >
    </ButtonComponent>

    <div class="draft-actions">
      <ButtonComponent
        v-if="hidePublishModal"
        :label="saveState.publish.label"
        :loading-label="$t('publishes')"
        :success-label="$t('published')"
        :theme="saveState.publish.theme"
        :promise="publishPromise"
        :class="{
          'elder-button--disabled': saveState.publish.disabled,
          'elder-button--invalid': saveState.publish.invalid,
        }"
        @click="
          () => {
            if (saveState.publish.disabled || saveState.publish.invalid) return
            publishPromise = publishDraft()
          }
        "
        v-tooltip="{ content: saveState.publish.tooltip }"
      >
      </ButtonComponent>

      <Action
        v-else
        :item="saveState.publish.disabled || saveState.publish.invalid ? null : item"
        :invalid="saveState.publish.invalid"
        type="publish"
        :action="publishDraft"
        v-tooltip="{ content: saveState.publish.tooltip }"
      />

      <transition name="fade">
        <DropdownComponent v-if="value && Object.keys(item).length">
          <ButtonComponent
            :icon="['fal', saveState.publish.icon]"
            :theme="saveState.publish.theme"
            :class="{
              'elder-button--disabled': saveState.publish.disabled,
              'elder-button--invalid': saveState.publish.invalid,
            }"
            :disabled="saveState.publish.disabled || saveState.publish.invalid"
          />
          <template #dropdown>
            <strong>{{ $tc('action', 2) }}</strong>
            <Action :item="item" type="reset" :action="removeDraft" />

            <template v-if="$store.getters.isSuperadmin && $store.getters.isDeveloper">
              <strong>Superadmin</strong>
              <Action :item="item" type="publish" :advanced="true" :action="publishDraft" />
              <Action :item="item" type="reset" :advanced="true" :action="removeDraft" />
            </template>
          </template>
        </DropdownComponent>
      </transition>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { DropdownComponent } from 'vue-elder-dropdown'
import Action from './Action.vue'
import { mapState, mapActions } from 'vuex'
import DraftApi from '@/api/Draft'
import StorageMixin from '@/mixins/Storage'

export default {
  mixins: [
    StorageMixin({
      target: 'hidePublishModal',
      key() {
        return `hide:publish:modal`
      },
      storage: 'localStorage',
    }),
  ],
  props: {
    value: [String, Boolean],
    submit: Function,
    saveState: Object,
    reference: Object, // { ref, onModel }
  },

  data() {
    return {
      hidePublishModal: false,
      savePromise: null,
      publishPromise: null,
    }
  },

  watch: {
    value: {
      immediate: true,
      handler() {
        this.fetch()
      },
    },
  },

  computed: {
    ...mapState('Draft', ['item']),
  },
  methods: {
    moment,
    ...mapActions('Draft', {
      fetch(dispatch) {
        if (['true', true, 'false', false, null].includes(this.value)) return
        return dispatch('fetch', [{ id: this.value }])
      },
      mutate: 'mutate',
      remove: 'remove',
    }),

    async saveDraft() {
      if (this.saveState.save.disabled || this.saveState.save.invalid) return

      if (!this.value) {
        await this.mutate([{ action: 'update', reference: this.reference }]).then(data => {
          this.$emit('input', data.id)
        })
      }

      await this.submit()
    },

    async publishDraft(data = {}) {
      await this.submit()

      if (this.value) {
        DraftApi.publish(this.value, data).then(() => {
          this.$emit('input', null)
        })
      }

      if (data.hidePublishModal) {
        setTimeout(() => {
          this.hidePublishModal = true
        }, 1000)
      }
    },

    removeDraft(data) {
      if (!this.value) return Promise.resolve()
      return DraftApi.reset(this.value, data).then(() => {
        this.$emit('input', null)
        this.$emit('reset')
      })
    },
  },

  components: {
    DropdownComponent,
    Action,
  },
}
</script>

<style lang="scss">
.draft {
  &-actions {
    display: flex;

    &:has(:not(:only-child)) {
      & > :not(:last-child) {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
        & > * {
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        }
      }

      & > :last-child.elder-dropdown {
        border-left: 1px solid white;

        & .elder-dropdown__trigger button {
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
        }
      }
    }
  }
}
</style>
